import { GroupByMode } from '@griply/types';
import { buildTodayReorderUserSettings } from '@griply/utils';
import { endOfDay } from 'date-fns';
import { useUpdateUserSettings } from 'features/user';
import { useCallback } from 'react';
import { useToday } from 'shared/contexts/today';
import { useUpdateTasksMutation } from 'shared/hooks/queries/use-update-tasks-mutation';
import { useUpdateRelationshipsReferences } from 'shared/hooks/use-update-relationships-references';
import { useUser } from 'shared/hooks/use-user';
import { ID } from 'shared/types/id';
import { RelationshipUpdate } from 'shared/types/relationship';
import { UpdateTask } from 'shared/types/task';

export const useTodayOnReorder = (groupBy: GroupByMode | undefined | null) => {
  const updateSettings = useUpdateUserSettings();
  const { mutate: updateTasks } = useUpdateTasksMutation();
  const updateRelationships = useUpdateRelationshipsReferences();
  const today = useToday();
  const user = useUser();
  const todoSorting = user?.settings?.todoSorting;

  return useCallback(
    (
      updatedSections: { id: ID; taskSorting: ID[] }[],
      updatedTasks: UpdateTask[],
      relationshipUpdates?: RelationshipUpdate[],
    ) => {
      if (updatedTasks.length) {
        updateTasks(updatedTasks);
      }

      // None's flat order has no date-keyed legacy; clear the today buckets of the
      // shared `todoSorting` it spans (Upcoming's future buckets stay intact).
      const legacyTodoSortingKeysToClear =
        groupBy === GroupByMode.None && todoSorting
          ? Object.keys(todoSorting).filter((key) => {
              const date = new Date(key);
              return (
                !Number.isNaN(date.getTime()) &&
                date.getTime() <= endOfDay(today).getTime()
              );
            })
          : undefined;

      const settingsUpdate = buildTodayReorderUserSettings({
        updatedSections,
        groupBy,
        legacyTodoSortingKeysToClear,
      });
      if (settingsUpdate) {
        updateSettings(settingsUpdate);
      }

      if (relationshipUpdates?.length) {
        updateRelationships(relationshipUpdates);
      }
    },
    [
      groupBy,
      today,
      todoSorting,
      updateSettings,
      updateTasks,
      updateRelationships,
    ],
  );
};
